import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TEAMS, teamLogo } from "@/lib/teams";
import { Loader2, Newspaper, ExternalLink, RefreshCw, Activity, ChevronDown } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function statusStyle(status) {
  const s = (status || "").toLowerCase();
  if (/out|reserve|\bir\b|pup|suspend|non-football/.test(s)) return "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40";
  if (/doubtful/.test(s)) return "bg-[#F97316]/15 text-[#F97316] border-[#F97316]/40";
  if (/question/.test(s)) return "bg-[#EAB308]/15 text-[#EAB308] border-[#EAB308]/40";
  return "bg-white/10 text-white/60 border-white/20";
}

function TeamLogo({ abbr, className }) {
  const [err, setErr] = useState(false);
  if (!abbr || err) return null;
  return <img src={teamLogo(abbr)} alt={abbr} onError={() => setErr(true)} className={className} />;
}

export default function News() {
  const { user } = useAuth();
  const [view, setView] = useState("headlines");
  const [team, setTeam] = useState(user?.avatar || "");
  const [articles, setArticles] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const teamParam = team ? `?team=${team}` : "";

  const load = (isRefresh) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    const path = view === "injuries" ? `/injuries${teamParam}` : `/news${teamParam}`;
    api.get(path)
      .then((res) => {
        setError("");
        if (view === "injuries") setInjuries(res.data.teams || []);
        else setArticles(res.data.articles || []);
      })
      .catch(() => setError("Couldn't load this feed right now. Try again shortly."))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(true), 300000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, team]);

  const teamOptions = useMemo(() => Object.keys(TEAMS).sort(), []);
  const [lead, ...rest] = articles;
  const injuredTeams = injuries.filter((t) => t.count > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="news-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <Newspaper className="w-7 h-7 text-[#E31837]" />
            <h1 className="font-['Bebas_Neue'] text-5xl tracking-tight leading-none">NFL NEWS</h1>
          </div>
          <p className="text-white/50 text-sm mt-1">Breaking headlines & the weekly injury report, updated live.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              className="appearance-none bg-[#141414] border border-white/10 pl-3 pr-9 py-2 rounded-sm text-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none"
              data-testid="news-team-filter"
            >
              <option value="">All Teams</option>
              {teamOptions.map((t) => (
                <option key={t} value={t}>{t} · {TEAMS[t].name}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2.5 top-2.5 pointer-events-none text-white/50" />
          </div>
          <button
            onClick={() => load(true)} disabled={refreshing}
            className="flex items-center gap-2 border border-white/20 hover:border-white/40 px-4 py-2 rounded-sm text-xs uppercase tracking-widest transition-colors duration-200 disabled:opacity-60"
            data-testid="news-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border border-white/10 w-fit">
        {[["headlines", "Headlines", Newspaper], ["injuries", "Injury Report", Activity]].map(([v, l, Icon]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-200 ${
              view === v ? "bg-[#E31837] text-white" : "text-white/50 hover:text-white"
            }`}
            data-testid={`news-tab-${v}`}
          >
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {team && (
        <div className="flex items-center gap-2 mb-5 text-sm text-white/60" data-testid="news-active-team">
          <TeamLogo abbr={team} className="w-6 h-6 object-contain" />
          Showing <span className="text-white font-semibold">{TEAMS[team]?.city} {TEAMS[team]?.name}</span>
          <button onClick={() => setTeam("")} className="text-[#E31837] hover:underline text-xs uppercase tracking-wider ml-1" data-testid="news-clear-team">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 py-20 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div className="bg-[#141414] border border-white/10 px-6 py-16 text-center" data-testid="news-error">
          <p className="font-['Bebas_Neue'] text-3xl tracking-tight">{error}</p>
        </div>
      ) : view === "headlines" ? (
        articles.length === 0 ? (
          <div className="bg-[#141414] border border-white/10 px-6 py-16 text-center text-white/50" data-testid="news-empty">No headlines available right now.</div>
        ) : (
          <>
            {lead && (
              <a href={lead.link} target="_blank" rel="noopener noreferrer"
                className="group block relative overflow-hidden border border-white/10 mb-6 hover:border-[#E31837]/50 transition-colors duration-300" data-testid="news-lead">
                <div className="grid md:grid-cols-2">
                  {lead.image ? (
                    <div className="relative h-56 md:h-72 overflow-hidden">
                      <img src={lead.image} alt={lead.headline || "NFL news"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : <div className="h-56 md:h-72 bg-[#141414]" />}
                  <div className="p-6 md:p-8 flex flex-col justify-center bg-[#141414]">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#E31837] mb-3">{lead.type || "Story"} · {timeAgo(lead.published)}</div>
                    <h2 className="font-['Bebas_Neue'] text-3xl md:text-4xl tracking-tight leading-tight mb-3 text-white/90 group-hover:text-white">{lead.headline}</h2>
                    {lead.description && <p className="text-sm text-white/50 line-clamp-3">{lead.description}</p>}
                    <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/70 group-hover:text-[#E31837] transition-colors duration-200 mt-4">Read full story <ExternalLink className="w-3.5 h-3.5" /></span>
                  </div>
                </div>
              </a>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rest.map((a, i) => (
                <a key={i} href={a.link} target="_blank" rel="noopener noreferrer"
                  className="group flex flex-col border border-white/10 bg-[#141414] hover:border-[#E31837]/50 transition-colors duration-300" data-testid={`news-card-${i}`}>
                  {a.image ? (
                    <div className="h-40 overflow-hidden">
                      <img src={a.image} alt={a.headline || "NFL news"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : <div className="h-40 bg-[#0A0A0A] flex items-center justify-center"><Newspaper className="w-8 h-8 text-white/10" /></div>}
                  <div className="p-4 flex flex-col flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{a.type || "Story"} · {timeAgo(a.published)}</div>
                    <h3 className="font-['Bebas_Neue'] text-xl tracking-wide leading-tight text-white/90 group-hover:text-white mb-2">{a.headline}</h3>
                    {a.description && <p className="text-xs text-white/45 line-clamp-2">{a.description}</p>}
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/60 group-hover:text-[#E31837] transition-colors duration-200 mt-3">Read <ExternalLink className="w-3 h-3" /></span>
                  </div>
                </a>
              ))}
            </div>
          </>
        )
      ) : (
        // Injury report
        injuredTeams.length === 0 ? (
          <div className="bg-[#141414] border border-white/10 px-6 py-16 text-center text-white/50" data-testid="injuries-empty">
            {team ? `No injuries reported for the ${TEAMS[team]?.name}.` : "No injuries reported right now."}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2" data-testid="injuries-list">
            {injuredTeams.map((t) => (
              <div key={t.abbr || t.displayName} className="border border-white/10 bg-[#141414]" data-testid={`injury-team-${t.abbr}`}>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#0A0A0A]">
                  <TeamLogo abbr={t.abbr} className="w-7 h-7 object-contain" />
                  <span className="font-['Bebas_Neue'] text-2xl tracking-wide leading-none flex-1">{t.displayName}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t.count} listed</span>
                </div>
                <div className="divide-y divide-white/5">
                  {t.players.map((p, i) => (
                    <div key={i} className="flex items-start gap-3 px-4 py-2.5" data-testid={`injury-player-${t.abbr}-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{p.name}</span>
                          {p.position && <span className="text-[10px] uppercase tracking-wider text-white/40">{p.position}</span>}
                        </div>
                        {p.comment && <p className="text-xs text-white/40 mt-0.5 line-clamp-2">{p.comment}</p>}
                      </div>
                      <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm ${statusStyle(p.status)}`}>{p.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
